import { getDBAsync } from '@/db';
import type { Team, TeamWithPlayers } from '@/types';
import { generateId } from '@/lib/utils';
import { playerRepo } from './playerRepo';

export const teamRepo = {
  /**
   * Get all teams
   */
  async getAll(): Promise<Team[]> {
    const db = await getDBAsync();
    return db.getAll('teams');
  },

  /**
   * Get all teams with player details
   */
  async getAllWithPlayers(): Promise<TeamWithPlayers[]> {
    const teams = await this.getAll();
    const result: TeamWithPlayers[] = [];

    for (const team of teams) {
      const player1 = await playerRepo.getById(team.player1Id);
      const player2 = await playerRepo.getById(team.player2Id);

      if (player1 && player2) {
        result.push({
          ...team,
          player1,
          player2,
        });
      }
    }

    return result;
  },

  /**
   * Get a team by ID
   */
  async getById(id: string): Promise<Team | undefined> {
    const db = await getDBAsync();
    return db.get('teams', id);
  },

  /**
   * Get a team with player details
   */
  async getWithPlayers(id: string): Promise<TeamWithPlayers | null> {
    const team = await this.getById(id);
    if (!team) {
      return null;
    }

    const player1 = await playerRepo.getById(team.player1Id);
    const player2 = await playerRepo.getById(team.player2Id);

    if (!player1 || !player2) {
      return null;
    }

    return {
      ...team,
      player1,
      player2,
    };
  },

  /**
   * Create a new team
   */
  async create(name: string, player1Id: string, player2Id: string): Promise<Team> {
    const db = await getDBAsync();
    const now = new Date();
    const team: Team = {
      id: generateId(),
      name: name.trim(),
      player1Id,
      player2Id,
      createdAt: now,
      updatedAt: now,
    };
    await db.put('teams', team);
    return team;
  },

  /**
   * Update a team
   */
  async update(
    id: string,
    updates: { name?: string; player1Id?: string; player2Id?: string }
  ): Promise<Team | null> {
    const db = await getDBAsync();
    const existing = await db.get('teams', id);
    if (!existing) {
      return null;
    }

    const updated: Team = {
      ...existing,
      name: updates.name?.trim() ?? existing.name,
      player1Id: updates.player1Id ?? existing.player1Id,
      player2Id: updates.player2Id ?? existing.player2Id,
      updatedAt: new Date(),
    };
    await db.put('teams', updated);
    return updated;
  },

  /**
   * Delete a team
   */
  async delete(id: string): Promise<boolean> {
    const db = await getDBAsync();
    const existing = await db.get('teams', id);
    if (!existing) {
      return false;
    }
    await db.delete('teams', id);
    return true;
  },

  /**
   * Search teams by name
   */
  async searchByName(query: string): Promise<Team[]> {
    const db = await getDBAsync();
    const all = await db.getAll('teams');
    const lowerQuery = query.toLowerCase();
    return all.filter((t) => t.name.toLowerCase().includes(lowerQuery));
  },

  /**
   * Check if a team name already exists
   */
  async nameExists(name: string, excludeId?: string): Promise<boolean> {
    const db = await getDBAsync();
    const all = await db.getAll('teams');
    const lowerName = name.toLowerCase().trim();
    return all.some(
      (t) => t.name.toLowerCase() === lowerName && t.id !== excludeId
    );
  },

  /**
   * Get teams containing a specific player
   */
  async getByPlayerId(playerId: string): Promise<Team[]> {
    const all = await this.getAll();
    return all.filter(
      (t) => t.player1Id === playerId || t.player2Id === playerId
    );
  },

  /**
   * Check if a team with the same players already exists
   */
  async playersExist(
    player1Id: string,
    player2Id: string,
    excludeId?: string
  ): Promise<boolean> {
    const all = await this.getAll();
    return all.some(
      (t) =>
        t.id !== excludeId &&
        ((t.player1Id === player1Id && t.player2Id === player2Id) ||
          (t.player1Id === player2Id && t.player2Id === player1Id))
    );
  },
};
